import {
  P2GAdaptiveOverlay,
  P2GAlert,
  P2GBox,
  P2GButton,
  P2GDivider,
  P2GGrid,
  P2GLink,
  P2GText,
  P2GTextField
} from '@parcel2go/core-ui';
import * as Sentry from '@sentry/react';
import { FormEvent, useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { TranslationNamespaces } from '~/lib/constants';
import { captureException } from '~/lib/metrics';
import { ActionType, IntegrationType } from '../../../lib/api/account-linking/types';

interface Props {
  integrationSlug: string;
  integrationType: IntegrationType;
  action: ActionType;
  accountId: string;
}

const FORM_DEFAULTS = {
  message: ''
};

const getStringClaim = (claim: unknown) => (typeof claim === 'string' && claim.trim() ? claim.trim() : undefined);

export default function BetaIntegrationFeedback(props: Props) {
  const { t } = useTranslation(TranslationNamespaces.ACCOUNT_LINKING);
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [formValues, setFormValues] = useState(FORM_DEFAULTS);
  const [stage, setStage] = useState<'INITIAL' | 'SUBMITTING' | 'ERROR' | 'COMPLETE'>('INITIAL');
  const [showMessageError, setShowMessageError] = useState(false);

  const feedbackName =
    getStringClaim(user?.profile?.name) ||
    [getStringClaim(user?.profile?.given_name), getStringClaim(user?.profile?.family_name)].filter(Boolean).join(' ') ||
    undefined;
  const feedbackEmail = getStringClaim(user?.profile?.email) || getStringClaim(user?.profile?.preferred_username);

  const close = useCallback(() => {
    setOpen(false);
    setStage('INITIAL');
    setShowMessageError(false);
  }, []);

  const openFeedback = useCallback(() => {
    setOpen(true);
  }, []);

  const submitFeedback = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      if (!formValues.message.trim()) {
        setShowMessageError(true);
        return;
      }

      try {
        setStage('SUBMITTING');

        const eventId = Sentry.captureMessage('SmartSend beta integration feedback', {
          level: 'info',
          tags: {
            area: 'account-linking',
            feedbackType: 'beta-integration',
            integrationSlug: props.integrationSlug,
            integrationType: props.integrationType,
            action: props.action
          }
        });

        await Sentry.captureFeedback({
          associatedEventId: eventId,
          name: feedbackName,
          email: feedbackEmail,
          url: window.location.href,
          message: JSON.stringify({
            message: formValues.message,
            integrationSlug: props.integrationSlug,
            integrationType: props.integrationType,
            action: props.action,
            accountId: props.accountId
          }),
          tags: {
            area: 'account-linking',
            feedbackType: 'beta-integration',
            integrationSlug: props.integrationSlug,
            integrationType: props.integrationType,
            action: props.action
          }
        });

        setStage('COMPLETE');
        setFormValues(FORM_DEFAULTS);
      } catch (e) {
        setStage('ERROR');
        captureException(e, 'Error in BetaIntegrationFeedback submitFeedback', {
          section: 'account-linking',
          integrationSlug: props.integrationSlug,
          integrationType: props.integrationType,
          action: props.action
        });
      }
    },
    [
      feedbackEmail,
      feedbackName,
      formValues.message,
      props.accountId,
      props.action,
      props.integrationSlug,
      props.integrationType
    ]
  );

  return (
    <>
      <P2GAlert color="warning">
        <P2GText component="p">
          <Trans
            t={t}
            i18nKey="BETA_INTEGRATION_WITH_FEEDBACK"
            defaults="This integration is currently in beta so please check imports for accuracy. If something is not working as expected, <a>please report it here</a> so we can fix it."
            components={{ a: <P2GLink onClick={openFeedback} /> }}
          />
        </P2GText>
      </P2GAlert>
      <P2GAdaptiveOverlay
        isOpen={open}
        onClose={close}
        modalTitle=""
        closeButtonText={t('CLOSE_DRAWER', { defaultValue: 'Close' })}
      >
        <P2GBox p={2}>
          {stage === 'COMPLETE' ? (
            <P2GGrid container direction="column" spacing={4}>
              <P2GGrid item>
                <P2GText variant="h2">
                  {t('BETA_FEEDBACK_SUCCESS_TITLE', { defaultValue: 'Thanks for your feedback' })}
                </P2GText>
              </P2GGrid>
              <P2GGrid item>
                <P2GText component="p">
                  {t('BETA_FEEDBACK_SUCCESS_MESSAGE', {
                    defaultValue: 'We have received your feedback and will use it to improve this integration.'
                  })}
                </P2GText>
              </P2GGrid>
              <P2GGrid item>
                <P2GButton color="secondary" size="large" onClick={close}>
                  {t('BETA_FEEDBACK_CLOSE', { defaultValue: 'Close' })}
                </P2GButton>
              </P2GGrid>
            </P2GGrid>
          ) : (
            <form onSubmit={submitFeedback}>
              <P2GGrid container direction="column" spacing={2}>
                <P2GGrid item>
                  <P2GText variant="h2">
                    {t('BETA_FEEDBACK_TITLE', { defaultValue: 'Beta integration feedback' })}
                  </P2GText>
                </P2GGrid>
                <P2GGrid item>
                  <P2GText component="p">
                    {t('BETA_FEEDBACK_MESSAGE', {
                      defaultValue:
                        'Tell us what is not working as expected and we will use it to improve this integration.'
                    })}
                  </P2GText>
                </P2GGrid>
                {stage === 'ERROR' && (
                  <P2GGrid item>
                    <P2GAlert color="error">
                      <P2GText component="p">
                        {t('BETA_FEEDBACK_ERROR', {
                          defaultValue: 'Sorry, we could not send your feedback. Please try again.'
                        })}
                      </P2GText>
                    </P2GAlert>
                  </P2GGrid>
                )}
                <P2GGrid item>
                  <P2GTextField
                    id="txtBetaIntegrationFeedback"
                    label={t('BETA_FEEDBACK_FIELD_LABEL', { defaultValue: 'What is not working as expected?' })}
                    multiline
                    rows={4}
                    fullWidth
                    dataTestId="beta-integration-feedback-message"
                    required
                    value={formValues.message}
                    onChange={(e) => {
                      setShowMessageError(false);
                      setFormValues({ message: e.target.value });
                    }}
                    error={showMessageError}
                  />
                </P2GGrid>
                <P2GGrid item>
                  <P2GDivider />
                </P2GGrid>
                <P2GGrid item>
                  <P2GButton
                    color="secondary"
                    type="submit"
                    size="large"
                    disabled={stage === 'SUBMITTING' || !formValues.message.trim()}
                  >
                    {t('BETA_FEEDBACK_SUBMIT', { defaultValue: 'Send feedback' })}
                  </P2GButton>
                </P2GGrid>
              </P2GGrid>
            </form>
          )}
        </P2GBox>
      </P2GAdaptiveOverlay>
    </>
  );
}
